/**
 * Conversation Controller — Lit reactive controller wrapping @elevenlabs/client.
 *
 * Owns the lifecycle of a WebRTC conversation:
 *   idle -> connecting -> active -> ended | error
 *   (or unavailable if the server reports the owner is out of minutes)
 *
 * Notifies its host element through `requestUpdate()` so re-render happens
 * automatically when state changes. All side-effects (fetch, mic permission,
 * EL handshake) are gated behind `start()` — never run at mount time.
 */

import type { ReactiveController, ReactiveControllerHost } from "lit";
import { Conversation, type Callbacks } from "@elevenlabs/client";

import {
  EmbedApiClient,
  type SessionModality,
  type EmbedConfigResponse,
  type WidgetSettingsPayload,
} from "./api-client.js";
import { ChatThread, type TranscriptEntry } from "./chat-thread.js";
import { ContextStore } from "./context-store.js";
import { EmbedApiError, validateEmbedVariables, EMBED_VARIABLE_MAX_LENGTH } from "../../../shared-types/src/utils/embed-dynamic-variables.js";
import { failureProps, Telemetry } from "./telemetry.js";

export type { TranscriptEntry } from "./chat-thread.js";
import { logger } from "../utils/logger.js";
import { isSecureContext, isWebRTCSupported } from "../utils/browser-checks.js";

export type ConversationState =
  | "idle"
  | "loading_config"
  | "unavailable"
  /** Waiting on the browser's microphone prompt, before any session exists. */
  | "requesting_mic"
  | "connecting"
  | "active"
  | "ended"
  | "error";

/** A session is a call or a chat, decided when it starts. */
export type { SessionModality } from "./api-client.js";

interface ConversationControllerOptions {
  agentId: string;
  serverUrl: string;
  context?: ContextStore;
}

export class ConversationController implements ReactiveController {
  state: ConversationState = "idle";
  config: EmbedConfigResponse | null = null;
  widgetSettings: WidgetSettingsPayload | null = null;
  errorMessage: string | null = null;
  isMicMuted = false;
  // Visual hint for the avatar: idle when neither speaking; speaking when EL
  // is producing audio.
  agentMode: "speaking" | "listening" = "listening";
  /**
   * Modality of the session being opened, running, or just finished.
   *
   * Kept through `ended` and `error` so the panel can label the outcome as a
   * chat or a call; cleared by `reset()` and overwritten by the next `start()`.
   */
  activeModality: SessionModality | null = null;
  /**
   * The line the agent opens with, from the config. Seeds the written
   * transcript so the greeting is on screen before anyone writes; the first
   * agent turn of a chat then takes its place so it is not shown twice.
   */
  firstMessage: string | null = null;
  /** The written thread: bubbles, the queued message, the reply awaited. */
  readonly thread = new ChatThread();
  lastError: EmbedApiError | null = null;

  private readonly host: ReactiveControllerHost;
  private readonly api: EmbedApiClient;
  readonly context: ContextStore;
  /** Funnel events. No-ops for the in-builder preview, which has no agent id. */
  readonly telemetry: Telemetry;
  private conversation: Conversation | null = null;
  private sessionId: string | null = null;
  private conversationId: string | null = null;
  private startedAt: number | null = null;
  // True when end() was called explicitly by the user — distinguishes user-initiated
  // disconnects from WebRTC infrastructure failures in onDisconnect.
  private _userRequestedEnd = false;
  private _retryCount = 0;
  private static readonly MAX_RETRIES = 2;
  private _retryTimer: ReturnType<typeof setTimeout> | null = null;
  private attemptVersion = 0;
  private detached = false;

  // ── Custom AnalyserNode for agent output audio ─────────────────────────
  // @elevenlabs/client@0.5.x WebRTC mode plays agent audio via a LiveKit-
  // attached <audio> element that bypasses the SDK's internal analyser, so
  // getOutputByteFrequencyData() always returns silence. We work around it
  // by wiring our own MediaElementAudioSourceNode → AnalyserNode → destination.
  private _agentAudioCtx: AudioContext | null = null;
  private _agentAnalyser: AnalyserNode | null = null;
  private _agentFreqData: Uint8Array<ArrayBuffer> | null = null;
  private _agentAudioObserver: MutationObserver | null = null;
  private _agentAudioTimeout: ReturnType<typeof setTimeout> | null = null;
  /**
   * Analyser graph per <audio> element.
   *
   * `createMediaElementSource()` may be called at most ONCE per element, and it
   * permanently reroutes that element's output through the graph — the audio is
   * only audible because of the `analyser -> destination` hop. So the chain is
   * built once and then left connected for the element's lifetime; tearing it
   * down would silence the agent, and rebuilding it would throw.
   */
  private readonly _agentGraphs = new WeakMap<
    HTMLAudioElement,
    { analyser: AnalyserNode; freqData: Uint8Array<ArrayBuffer> }
  >();

  constructor(
    host: ReactiveControllerHost,
    private readonly opts: ConversationControllerOptions,
  ) {
    this.host = host;
    this.context = opts.context ?? new ContextStore();
    this.api = new EmbedApiClient(opts.serverUrl);
    this.telemetry = new Telemetry(opts.agentId, opts.serverUrl);
    host.addController(this);
  }

  get transcript(): TranscriptEntry[] {
    return this.thread.entries;
  }

  /** True between a typed message and the agent's next written reply. */
  get awaitingReply(): boolean {
    return this.thread.awaitingReply;
  }

  /**
   * What is in the composer.
   *
   * Owned here rather than by the composer so the words survive whatever the
   * session does: a start that fails hands them back, and a send that is
   * refused leaves them where they were. Cleared only when a message is
   * accepted.
   */
  get draft(): string {
    return this.thread.draft;
  }

  set draft(text: string) {
    this.thread.draft = text;
  }

  // Config loading is NOT started here on purpose. The host element decides
  // where its config comes from — the server for a live embed, or injected
  // settings for the in-builder preview — and a controller that fetched on
  // connect would race that choice.
  hostConnected(): void {
    this.detached = false;
  }

  /**
   * Render from settings supplied by the caller instead of the server.
   *
   * The agent builder previews drafts that do not exist server-side yet, and
   * edits that have not been saved. Re-applying on every change is what makes
   * the preview live.
   */
  applyConfig(config: EmbedConfigResponse): void {
    this.config = config;
    this.widgetSettings = config.widgetSettings;
    this.firstMessage = config.firstMessage?.trim() || null;
    this.context.setAllowedKeys(config.allowedDynamicVars);
    this.seedTranscript();
    this.setState(config.available ? "idle" : "unavailable");
  }

  /**
   * Builder preview only: dress the widget as a running call or chat, with no
   * session behind it, so the operator sees every state before going live.
   * `null` puts it back at rest.
   */
  showPreviewSession(
    modality: SessionModality | null,
    visitorLine: string,
  ): void {
    this.thread.reset();
    this.activeModality = modality;
    this.errorMessage = null;
    if (modality === null) {
      this.seedTranscript();
      this.setState("idle");
      return;
    }
    this.thread.seed(this.firstMessage, this.context.snapshot());
    this.thread.append("user", visitorLine);
    this.thread.awaitingReply = modality === "text";
    this.agentMode = "speaking";
    this.setState("active");
  }

  hostDisconnected(): void {
    this.detached = true;
    this.attemptVersion++;
    this.lastError = new EmbedApiError("widget_not_connected");
    this.cancelRetry();
    this.thread.unqueue();
    const conversation = this.conversation;
    if (conversation || this.sessionId !== null) {
      void this.finishSession("user_ended");
      if (conversation) void this.closeConversation(conversation);
    }
    this._teardownAgentAudio();
  }

  /** Lazily refresh widget config (called on mount and after errors). */
  async loadConfig(): Promise<void> {
    const version = this.attemptVersion;
    this.setState("loading_config");
    const result = await this.api.getConfig(this.opts.agentId);
    if (this.detached || version !== this.attemptVersion) return;
    if (!result.ok) {
      this.lastError = new EmbedApiError(result.code, {
        ...result.details,
        status: result.status,
      });
      this.errorMessage = result.code;
      this.telemetry.track("web_widget_conversation_failed", failureProps(result.code));
      // 404 == "agent doesn't exist or isn't a public web agent"; hide.
      this.setState("unavailable");
      return;
    }

    this.config = result.value;
    this.widgetSettings = result.value.widgetSettings;
    this.firstMessage = result.value.firstMessage?.trim() || null;
    this.context.setAllowedKeys(result.value.allowedDynamicVars);
    this.seedTranscript();

    if (!result.value.available) {
      // Owner out of minutes — hide widget entirely (decision D11).
      this.setState("unavailable");
      return;
    }

    // The impression: the widget is configured, in quota and about to be
    // visible. Fired here rather than on mount so a widget nobody could have
    // seen never enters the funnel as one that was ignored.
    this.telemetry.track("web_widget_loaded", {
      modality: this.widgetSettings?.modality ?? "voice",
    });
    this.setState("idle");
  }

  /**
   * Begin a call or a chat.
   *
   * `resume` belongs to the reconnect after a chat dropped: it continues the
   * conversation already on screen instead of opening a new one. It is an
   * argument rather than a flag on the controller because anything remembered
   * between calls is eventually read by the wrong one.
   */
  async start(
    modality: SessionModality = "voice",
    options: {
      resume?: boolean;
      dynamicVariables?: Record<string, unknown>;
      /** A reconnect must not pick up context from a different page visitor. */
      retryVariables?: Record<string, string>;
      /** The new imperative API opts in; existing UI preserves legacy starts. */
      strictVariables?: boolean;
    } = {},
  ): Promise<boolean> {
    this.lastError = null;
    if (
      this.state === "connecting" ||
      this.state === "active" ||
      this.state === "requesting_mic"
    ) {
      return false;
    }
    if (this.detached) {
      this.lastError = new EmbedApiError("widget_not_connected");
      return false;
    }
    // A new visitor action supersedes any older scheduled reconnect, even if
    // this attempt fails before opening a session. Automatic retries keep their budget.
    if (options.retryVariables === undefined) this.cancelRetry();
    const attempt = ++this.attemptVersion;
    const isCurrent = () => !this.detached && attempt === this.attemptVersion;
    const cancelled = (): false => {
      if (options.retryVariables === undefined)
        throw new EmbedApiError(
          this.detached ? "widget_not_connected" : "session_start_cancelled",
        );
      return false;
    };
    const isText = modality === "text";
    // A chat runs over a WebSocket and never touches the microphone, so the
    // two checks that gate a call have nothing to say about it.
    if (!isText && (!isWebRTCSupported() || !isSecureContext())) {
      this.errorMessage = "webrtc_unsupported";
      this.telemetry.track("web_widget_conversation_failed", failureProps("webrtc_unsupported"));
      this.setState("error");
      return false;
    }

    const resuming = isText && options.resume === true;
    // One immutable input per attempt: setContext during the microphone prompt
    // or token request must not change what reaches the provider mid-handshake.
    let requestedVariables: Record<string, string>;
    try {
      requestedVariables =
        options.retryVariables ??
        (options.strictVariables
          ? this.context.validatedSnapshot(
              this.config?.requiredDynamicVars ?? [],
              options.dynamicVariables,
            )
          : this.context.snapshot(options.dynamicVariables));
      if (!options.strictVariables)
        validateEmbedVariables(
          Object.fromEntries(
            (this.config?.requiredGreetingVars ?? []).flatMap((key) =>
              requestedVariables[key] === undefined ? [] : [[key, requestedVariables[key].slice(0, EMBED_VARIABLE_MAX_LENGTH)]],
            ),
          ),
          this.config?.allowedDynamicVars ?? Object.keys(requestedVariables),
          this.config?.requiredGreetingVars ?? [],
        );
    } catch (error) {
      if (!(error instanceof EmbedApiError)) throw error;
      this.lastError = error;
      this.errorMessage = error.code;
      this.thread.unqueue();
      this.setState("error");
      return false;
    }
    this.errorMessage = null;
    this._userRequestedEnd = false;
    this.activeModality = modality;
    // A chat opens on the greeting, and on the message that opened it; a call
    // starts from an empty transcript, as it always has. A reconnect keeps what
    // was said: the visitor is in the middle of a conversation, not starting one.
    if (resuming) this.thread.settleGreeting();
    else
      this.thread.seed(isText ? this.firstMessage : null, requestedVariables);

    // Ask for the microphone before opening a session. The browser prompt is
    // the single most likely place to lose someone, and doing it first means a
    // refusal costs nothing: no token issued, no session to reconcile.
    if (!isText) {
      // Lock the attempt before the asynchronous permissions query too: two
      // calls from the host API must not both reach the microphone/token step.
      this.setState("requesting_mic");
      const permitted = await this.ensureMicPermission(isCurrent);
      if (!isCurrent()) return cancelled();
      if (!permitted) return false;
    }

    this.setState("connecting");

    const startResult = await this.api.startSession({
      agentId: this.opts.agentId,
      dynamicVariables: requestedVariables,
      modality,
      ...(options.strictVariables
        ? { variableValidation: "strict" as const }
        : {}),
    });

    if (!isCurrent()) {
      if (startResult.ok && startResult.value.sessionId !== null) {
        void this.api.endSession({
          agentId: this.opts.agentId,
          sessionId: startResult.value.sessionId,
          reason: "user_ended",
        });
      }
      return cancelled();
    }

    if (!startResult.ok) {
      this.lastError = new EmbedApiError(startResult.code, {
        ...startResult.details,
        status: startResult.status,
      });
      this.errorMessage = startResult.code || "network_error";
      logger.warn("session/start failed", startResult);
      this.thread.unqueue();
      this.host.requestUpdate();
      if (startResult.status === 402) {
        this.setState("unavailable");
        return false;
      }
      this.telemetry.track("web_widget_conversation_failed", failureProps(this.errorMessage));
      this.setState("error");
      return false;
    }

    this.sessionId = startResult.value.sessionId;
    // New servers return sanitized values. Against an older server, still use
    // the original attempt's snapshot narrowed to the keys it accepted.
    const sessionVariables =
      startResult.value.dynamicVariables ??
      Object.fromEntries(
        Object.entries(requestedVariables).filter(([key]) =>
          startResult.value.acceptedDynamicVars.includes(key),
        ),
      );
    if (!resuming)
      this.thread.seed(isText ? this.firstMessage : null, sessionVariables);

    let disconnected = false;
    let failed = false;
    try {
      // The SDK's SessionConfig is a discriminated union: a websocket session
      // needs a `signedUrl` and forbids a `conversationToken`, a WebRTC one the
      // reverse. The server issues the credential that matches the modality
      // it was asked for, so each branch below carries exactly one of them.
      const callbacks: Callbacks = {
        onConnect: ({ conversationId }) => {
          if (!isCurrent() || disconnected || failed) return;
          this.conversationId = conversationId;
          this.startedAt = Date.now();
          // Counted here rather than on the button press: a call is one that
          // actually connected, and conflating the two hides every failure
          // between the click and the audio.
          this.telemetry.track("web_widget_conversation_started", { modality });
          this.setState("active");
          this.flushThread();
        },
        onDisconnect: () => {
          if (!isCurrent() || disconnected || failed) return;
          disconnected = true;
          const durationMs = this.startedAt ? Date.now() - this.startedAt : 0;
          // If the disconnect fires within 5s of connecting and the user did NOT
          // explicitly click end, it's almost certainly a WebRTC/DataChannel crash.
          const isEarlyDrop = durationMs < 5_000 && !this._userRequestedEnd;
          if (isEarlyDrop) {
            this.errorMessage = "conversation_error";
            this.lastError = new EmbedApiError("conversation_error");
          }
          if (
            isEarlyDrop &&
            this._retryCount < ConversationController.MAX_RETRIES
          ) {
            this._retryCount++;
            const delay = this._retryCount * 1_500;
            logger.warn(
              `onDisconnect <5s — WebRTC crash, retrying (${this._retryCount}/${ConversationController.MAX_RETRIES}) in ${delay}ms`,
            );
            // Read before finishSession clears them: a chat that dropped with a
            // reply outstanding has a message nobody ever answered.
            const unanswered = modality === "text" && this.thread.awaitingReply;
            void this.finishSession("error");
            this._retryTimer = setTimeout(() => {
              if (!isCurrent()) return;
              this._retryTimer = null;
              if (unanswered) this.thread.requeueLastUserTurn();
              void this.start(modality, {
                resume: modality === "text",
                retryVariables: sessionVariables,
                strictVariables: options.strictVariables,
              });
            }, delay);
          } else {
            // An early drop with the retry budget spent is a failure, not a
            // goodbye. Ending it as "user_ended" would render the calm "call
            // ended" screen and hide the fact that nothing ever connected.
            if (isEarlyDrop) {
              logger.warn(
                "onDisconnect <5s — retries exhausted, surfacing error",
              );
              this.errorMessage = "conversation_error";
              this.telemetry.track("web_widget_conversation_failed", failureProps("conversation_error"));
            }
            void this.finishSession(isEarlyDrop ? "error" : "user_ended");
          }
        },
        onError: (message) => {
          if (!isCurrent() || disconnected || failed) return;
          failed = true;
          logger.error("Conversation error", message);
          this.errorMessage = "conversation_error";
          this.telemetry.track("web_widget_conversation_failed", failureProps("conversation_error"));
          this.lastError = new EmbedApiError("conversation_error");
          const conversation = this.conversation;
          void this.finishSession("error");
          if (conversation) void this.closeConversation(conversation);
        },
        onMessage: ({ message, source }) => {
          if (!isCurrent() || disconnected || failed) return;
          if (!message) return;
          if (source === "user" && this.thread.isEchoOfTypedTurn(message))
            return;
          // The greeting rule is a chat's: a call never seeds anything, and
          // must never lose an agent turn to it.
          if (source === "ai" && this.activeModality === "text") {
            this.thread.acceptAgentTurn(message);
          } else {
            this.thread.append(source, message);
          }
          this.host.requestUpdate();
        },
        onModeChange: ({ mode }) => {
          if (!isCurrent() || disconnected || failed) return;
          this.agentMode = mode;
          this.host.requestUpdate();
        },
      };

      let conversation: Conversation;
      if (isText) {
        const signedUrl = startResult.value.signedUrl;
        if (!signedUrl) throw new Error("text session without signedUrl");
        conversation = await Conversation.startSession({
          ...callbacks,
          dynamicVariables: sessionVariables,
          signedUrl,
          connectionType: "websocket",
          textOnly: true,
          overrides: { conversation: { textOnly: true } },
        });
        // The SDK fires onConnect from inside its constructor, before this
        // assignment: a queued message could not leave from there, because
        // there was nothing yet to send it through. Whichever of the two
        // happens second sends it; the thread makes sure it is sent once.
      } else {
        const conversationToken = startResult.value.conversationToken;
        if (!conversationToken)
          throw new Error("voice session without conversationToken");
        conversation = await Conversation.startSession({
          ...callbacks,
          dynamicVariables: sessionVariables,
          conversationToken,
          connectionType: "webrtc",
        });
        // Only a call has agent audio to analyse. Wire our own AnalyserNode to
        // the LiveKit-attached <audio> element so the waveform actually reacts
        // to agent speech in WebRTC mode.
      }
      // A late SDK result must never resurrect a removed widget, an ended
      // attempt, or a connection whose callbacks already reported failure.
      if (!isCurrent() || disconnected || failed) {
        void this.closeConversation(conversation);
        if (!isCurrent()) return cancelled();
        return false;
      }
      this.conversation = conversation;
      if (isText && this.isConnected) this.flushThread();
      if (!isText) this._watchForAgentAudio();
      // A provider callback can report failure before the SDK promise settles.
      return this.errorMessage === null;
    } catch (error) {
      if (!isCurrent()) return cancelled();
      logger.error("startSession threw", error);
      this.thread.unqueue();
      this.host.requestUpdate();
      this.errorMessage = isPermissionError(error)
        ? "mic_permission_denied"
        : "conversation_error";
      this.telemetry.track("web_widget_conversation_failed", failureProps(this.errorMessage));
      this.lastError = new EmbedApiError(this.errorMessage);
      void this.finishSession("error");
      return false;
    }
  }

  /**
   * Get the microphone before anything else needs it.
   *
   * The SDK would ask on its own, but from inside a connection attempt: the
   * visitor sees "Connecting…" while the browser quietly raises a permission
   * prompt they did not expect, and a refusal surfaces as a generic failure.
   * Asking up front turns that into a step with its own screen, and turns a
   * denial into an explanation they can act on.
   *
   * The stream is released immediately. The SDK opens its own, and holding a
   * second one leaves the browser's recording indicator lit for a call that
   * never started.
   */
  private async ensureMicPermission(
    isCurrent: () => boolean,
  ): Promise<boolean> {
    const granted = await isMicPermissionGranted();
    if (!isCurrent()) return false;
    if (granted) return true;

    this.setState("requesting_mic");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      for (const track of stream.getTracks()) track.stop();
      return isCurrent();
    } catch (error) {
      if (!isCurrent()) return false;
      logger.warn("microphone request failed", error);
      this.errorMessage = micErrorCode(error);
      this.telemetry.track("web_widget_conversation_failed", failureProps(this.errorMessage));
      this.setState("error");
      return false;
    }
  }

  /** Stop a reconnect that was scheduled but has not fired. */
  private cancelRetry(): void {
    if (this._retryTimer) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
    this._retryCount = 0;
  }

  /** End an in-progress call. */
  async end(): Promise<void> {
    this.attemptVersion++;
    this.lastError = new EmbedApiError("session_start_cancelled");
    this._userRequestedEnd = true;
    // Cancel any pending auto-retry so we don't reconnect after the user hangs up.
    this.cancelRetry();
    this.thread.unqueue();
    const conversation = this.conversation;
    if (conversation || this.sessionId !== null) {
      void this.finishSession("user_ended");
      if (conversation) await this.closeConversation(conversation);
    } else {
      // Conversation never started — go back to idle.
      this.setState("idle");
    }
  }

  /** Returns the agent's output FFT frequency data (0-255 per bin).
   *  Used by <deepagent-waveform> to render real-time audio visualization.
   *  Prefers our custom analyser (wired to the LiveKit <audio> element);
   *  falls back to the SDK's analyser which is silent in WebRTC mode but
   *  works in websocket mode if we ever switch.
   *  Returns null when no active session exists. */
  getOutputFrequencyData(): Uint8Array | null {
    if (this._agentAnalyser && this._agentFreqData) {
      this._agentAnalyser.getByteFrequencyData(this._agentFreqData);
      return this._agentFreqData;
    }
    return this.conversation?.getOutputByteFrequencyData() ?? null;
  }

  /** Returns the user's microphone FFT frequency data (0-255 per bin, 256 bins).
   *  Used by <deepagent-waveform> during agent "listening" mode. */
  getInputFrequencyData(): Uint8Array | null {
    return this.conversation?.getInputByteFrequencyData() ?? null;
  }

  toggleMute(): void {
    if (!this.conversation) return;
    this.isMicMuted = !this.isMicMuted;
    this.conversation.setMicMuted(this.isMicMuted);
    this.host.requestUpdate();
  }

  /**
   * Send a typed message.
   *
   * Inside a session it goes out at once. Outside one, before the first chat or
   * after a previous chat ended, it opens the session instead: writing is how a
   * chat starts, so there is no button to press first. Returns false, leaving
   * the draft untouched, when there is nothing to send and nothing that could
   * be opened; the draft clears only for a message that was taken.
   */
  sendMessage(text: string): boolean {
    const trimmed = text.trim();
    if (!trimmed) return false;
    if (this.state === "active" && this.conversation) {
      this.thread.draft = "";
      this.thread.queue(trimmed);
      this.flushThread();
      return true;
    }
    if (!this.canOpenChat) return false;
    // Someone writing beats a reconnect we had only scheduled: the chat they
    // are opening now is the one that matters, and two sessions racing to
    // become the same conversation is worse than the drop that caused it.
    this.cancelRetry();
    this.thread.draft = "";
    this.thread.queue(trimmed);
    // Not awaited: the bubble and the "connecting" state are already on screen
    // through the synchronous head of start(), and the caller is an event
    // handler that has nothing left to do.
    void this.start("text").catch((error: unknown) => {
      // A removed widget or a newer visitor action can cancel the queued start.
      // Its late result must not change the newer attempt's state or draft.
      if (!(error instanceof EmbedApiError))
        logger.error("chat start threw", error);
    });
    return true;
  }

  /**
   * True when typing may open a chat: the owner allows written sessions, and
   * none is running. `error` is included so a failed attempt can be retried by
   * pressing send again on the text that was handed back.
   */
  private get canOpenChat(): boolean {
    if (this.widgetSettings?.modality === "voice") return false;
    return (
      this.state === "idle" || this.state === "ended" || this.state === "error"
    );
  }

  /**
   * Read through a getter so a callback's state change is visible after an
   * `await`: the compiler narrows a field it saw assigned earlier in the
   * function, and would call the comparison unintentional.
   */
  private get isConnected(): boolean {
    return this.state === "active";
  }

  /** Send whatever the thread has queued, through the session that can carry it. */
  private flushThread(): void {
    const conversation = this.conversation;
    if (!conversation) return;
    if (!this.thread.flush((text) => conversation.sendUserMessage(text)))
      return;
    this.host.requestUpdate();
  }

  /**
   * Put the greeting on screen before anyone writes.
   *
   * Only for a widget that accepts writing, and only while no session has run:
   * a call renders its own transcript, and a finished chat keeps what was said.
   */
  private seedTranscript(): void {
    if (this.activeModality !== null) return;
    if (this.widgetSettings?.modality === "voice") return;
    this.thread.seed(this.firstMessage, this.context.snapshot());
  }

  /** Called by the host element after the panel collapses post-call. */
  reset(): void {
    this.attemptVersion++;
    const conversation = this.conversation;
    if (conversation || this.sessionId !== null) {
      void this.finishSession("user_ended");
      if (conversation) void this.closeConversation(conversation);
    }
    if (this._retryTimer) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
    this.thread.reset();
    this.errorMessage = null;
    this.sessionId = null;
    this.conversationId = null;
    this.startedAt = null;
    this._userRequestedEnd = false;
    this._retryCount = 0;
    this.activeModality = null;
    this.setState("idle");
    // Back to the greeting the next visitor-to-be reads on opening.
    this.seedTranscript();
  }

  private async finishSession(
    reason: "user_ended" | "error" | "timeout",
  ): Promise<void> {
    const ended = this.conversation;
    this.conversation = null;
    this.isMicMuted = false;
    // A message whose handshake failed was never sent. Return it to the
    // composer before sessionClosed clears pending, so it cannot be lost or
    // accidentally flushed by a later start with different variables.
    this.thread.unqueue();
    this.thread.sessionClosed();
    if (reason !== "error") {
      // Only reset on clean end; retries preserve _userRequestedEnd=false naturally.
      this._userRequestedEnd = false;
    }

    // Detach our custom analyser from the (about-to-be-removed) <audio> element.
    this._teardownAgentAudio();

    const durationSeconds = this.startedAt
      ? Math.max(0, Math.round((Date.now() - this.startedAt) / 1000))
      : 0;

    // Show error state for infrastructure failures so the user sees a retry prompt
    // rather than the "call ended" screen (which looks like a normal goodbye).
    this.setState(reason === "error" ? "error" : "ended");

    // Emit end telemetry once for an SDK session owned by this controller.
    if (ended) {
      this.telemetry.track("web_widget_conversation_ended", {
        duration_seconds: durationSeconds,
        reason,
        user_turns: this.thread.entries.filter((e) => e.role === "user" && !e.pending).length,
      });
    }

    // Close registered rows even when a cancelled/failed handshake has no SDK session.
    if (this.sessionId !== null) {
      void this.api.endSession({
        sessionId: this.sessionId,
        agentId: this.opts.agentId,
        conversationId: this.conversationId ?? undefined,
        durationSeconds,
        reason,
      });
    }
    this.sessionId = null;
    this.conversationId = null;
    this.startedAt = null;
  }

  private async closeConversation(conversation: Conversation): Promise<void> {
    try {
      await conversation.endSession();
    } catch (error) {
      logger.warn("endSession threw", error);
    }
  }

  private setState(next: ConversationState): void {
    if (next !== this.state) {
      this.state = next;
      this.host.requestUpdate();
    }
  }

  /**
   * Watch document.body for the LiveKit-attached agent <audio> element.
   * LiveKit's WebRTC track attach creates a hidden <audio autoplay> in
   * document.body. We hook it with our own AnalyserNode because the SDK's
   * internal output analyser is dead in WebRTC mode.
   */
  private _watchForAgentAudio(): void {
    if (typeof document === "undefined") return;

    // Try immediate scan (element may already be attached if startSession
    // resolved late).
    const existing = this._findAgentAudioEl();
    if (existing) {
      this._attachAgentAnalyser(existing);
      return;
    }

    // Otherwise observe additions to <body>.
    try {
      this._agentAudioObserver?.disconnect();
      this._agentAudioObserver = new MutationObserver(() => {
        const el = this._findAgentAudioEl();
        if (el) {
          this._attachAgentAnalyser(el);
          this._agentAudioObserver?.disconnect();
          this._agentAudioObserver = null;
        }
      });
      // subtree: LiveKit does not guarantee the element lands as a direct child
      // of <body>, and a nested attach would otherwise never be seen.
      this._agentAudioObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Safety net: give up after 10s if no <audio> appears.
      this._agentAudioTimeout = setTimeout(() => {
        if (this._agentAudioObserver && !this._agentAnalyser) {
          this._agentAudioObserver.disconnect();
          this._agentAudioObserver = null;
          logger.warn(
            "Agent <audio> element not found within 10s — waveform during agent speech will be flat",
          );
        }
      }, 10_000);
    } catch (error) {
      logger.warn("Failed to set up agent audio observer", error);
    }
  }

  /**
   * Returns the most recently added LiveKit-attached agent audio element.
   * Identified by: <audio> with autoplay, no controls, display:none. We pick
   * the LAST matching element since multiple calls in the same page would
   * leave stale tags around.
   */
  private _findAgentAudioEl(): HTMLAudioElement | null {
    if (typeof document === "undefined") return null;
    const candidates =
      document.body.querySelectorAll<HTMLAudioElement>("audio");
    for (let i = candidates.length - 1; i >= 0; i--) {
      const el = candidates[i];
      if (!el) continue;
      // A previously-wired element is still a valid target: its cached graph
      // gets reused rather than rebuilt.
      if (!el.autoplay) continue;
      const styleHidden =
        el.style.display === "none" || getComputedStyle(el).display === "none";
      if (!styleHidden) continue;
      return el;
    }
    return null;
  }

  /**
   * Wire a MediaElementAudioSourceNode → AnalyserNode → destination chain.
   * Preserves playback (the destination connection keeps audio audible).
   */
  private _attachAgentAnalyser(audioEl: HTMLAudioElement): void {
    try {
      // Reuse a single AudioContext across sessions to avoid hitting browser
      // limits (Chromium caps active AudioContexts at ~6).
      if (!this._agentAudioCtx || this._agentAudioCtx.state === "closed") {
        const Ctor =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext;
        if (!Ctor) return;
        this._agentAudioCtx = new Ctor();
      }
      const ctx = this._agentAudioCtx;

      // Resume if suspended (some browsers suspend new contexts even after
      // a user gesture if the gesture happened before context creation).
      if (ctx.state === "suspended") {
        void ctx.resume().catch(() => {});
      }

      // Reuse the graph if this element was already wired on an earlier call:
      // calling createMediaElementSource twice on the same element throws.
      const existing = this._agentGraphs.get(audioEl);
      if (existing) {
        this._agentAnalyser = existing.analyser;
        this._agentFreqData = existing.freqData;
        return;
      }

      const source = ctx.createMediaElementSource(audioEl);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.6;

      source.connect(analyser);
      analyser.connect(ctx.destination);

      // Allocate over a concrete ArrayBuffer to satisfy the strict type
      // signature of AnalyserNode.getByteFrequencyData (which requires
      // Uint8Array<ArrayBuffer>, not Uint8Array<ArrayBufferLike>).
      const freqData = new Uint8Array(
        new ArrayBuffer(analyser.frequencyBinCount),
      );

      this._agentGraphs.set(audioEl, { analyser, freqData });
      this._agentAnalyser = analyser;
      this._agentFreqData = freqData;
    } catch (error) {
      logger.warn("Failed to attach custom AnalyserNode to agent audio", error);
    }
  }

  /**
   * Stop reading agent audio at call end.
   *
   * Deliberately does NOT disconnect the source/analyser chain: the element's
   * audio flows through it, so disconnecting would leave a reused element
   * permanently silent on the next call. We only drop the read references; the
   * graph is cached per element in `_agentGraphs` and reused.
   */
  private _teardownAgentAudio(): void {
    try {
      this._agentAudioObserver?.disconnect();
    } catch {
      /* noop */
    }
    this._agentAudioObserver = null;

    if (this._agentAudioTimeout) {
      clearTimeout(this._agentAudioTimeout);
      this._agentAudioTimeout = null;
    }

    this._agentAnalyser = null;
    this._agentFreqData = null;

    // Keep the AudioContext alive between calls — closing it would force a
    // re-prompt for autoplay on some browsers. It will be GC'd on page unload.
  }
}

function isPermissionError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { name?: string; message?: string };
  return (
    e.name === "NotAllowedError" ||
    e.name === "PermissionDeniedError" ||
    (typeof e.message === "string" && /permission/i.test(e.message))
  );
}

/**
 * Distinguish the three ways a microphone request fails, because the fix is
 * different every time: change a browser setting, plug something in, or quit
 * the app that already holds the device. One generic message would send two
 * thirds of these visitors looking in the wrong place.
 */
function micErrorCode(error: unknown): string {
  const name =
    error && typeof error === "object"
      ? (error as { name?: string }).name
      : undefined;
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return "mic_not_found";
  }
  if (name === "NotReadableError" || name === "TrackStartError") {
    return "mic_unavailable";
  }
  return isPermissionError(error) ? "mic_permission_denied" : "mic_unavailable";
}

/**
 * True only when the browser can say the microphone is already allowed.
 *
 * Firefox and Safari do not implement the "microphone" permission descriptor
 * and throw, which we read as "ask properly" rather than as an error — a false
 * negative costs one extra screen, a false positive would strand the visitor
 * on a prompt that never comes.
 */
async function isMicPermissionGranted(): Promise<boolean> {
  try {
    const status = await navigator.permissions.query({
      name: "microphone" as PermissionName,
    });
    return status.state === "granted";
  } catch {
    return false;
  }
}
